import { DEFAULT_TIMEZONE } from '@yoyoclub/shared';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import AdminLayout, { type NavItem } from '../../components/admin/AdminLayout';
import { TOUR, tourAttr } from '../../features/hostTour';
import { useAuth } from '../../hooks/useAuth';
import { bilingualLines, localize } from '../../i18n/script';
import { useSiteLanguage } from '../../i18n/useSiteLanguage';

const hostNavItems: NavItem[] = [
  { to: '/host/activities', label: 'Activities' },
  { to: '/host/courses', label: 'Courses' },
  { to: '/host/passes', label: 'Passes' },
  { to: '/host/bookings', label: 'Bookings' },
  { to: '/host/email', label: 'Email' },
];

interface EmailApproval {
  id: string;
  hostName: string;
  hostEmail: string | null;
  sessionId: string;
  activityTitle: string;
  activityTitleEN: string | null;
  sessionStartTime: string;
  emailType: string;
  detailLink: string | null;
  customMessage: string | null;
  scheduledAt: string | null;
  createdAt: string;
}

interface Activity {
  id: string;
  titleCN: string | null;
  titleEN: string | null;
}

interface EmailRecipient {
  bookingId: string;
  name: string;
  email: string;
  sentAt: string;
}

interface EmailSession {
  id: string;
  activityId: string;
  activityTitleCN: string;
  activityTitleEN: string;
  startTime: string;
  endTime: string;
  isCancelled: boolean;
  isPast: boolean;
  detailLink: string | null;
  detailEmailScheduledAt: string | null;
  detailEmailSentAt: string | null;
  reminderScheduledAt: string | null;
  reminderSentAt: string | null;
  detailCustomMessage: string | null;
  reminderCustomMessage: string | null;
  confirmedCount: number;
  detailSentCount: number;
  reminderSentCount: number;
  hasPendingDetailApproval?: boolean;
  hasPendingReminderApproval?: boolean;
  wasReviewedDetail?: boolean;
  wasReviewedReminder?: boolean;
}

const EPOCH_SENTINEL = '1970-01-01T00:00:00.000Z';

function isProcessing(scheduledAt: string | null): boolean {
  if (!scheduledAt) return false;
  return new Date(scheduledAt).getTime() === new Date(EPOCH_SENTINEL).getTime();
}

interface BulkErrorDetail {
  sessionId: string;
  startTime: string;
  reason: string;
}

const BULK_REASON_LABELS: Record<string, string> = {
  CANCELLED: 'Cancelled',
  SESSION_STARTED: 'Session started',
  ALREADY_SENT: 'Already sent',
  SENDING: 'Sending',
  PENDING_APPROVAL: 'Pending review',
  LOCKED_BY_REVIEW: 'Locked by admin review',
  ALREADY_SCHEDULED: 'Scheduled by admin',
  STATE_CHANGED: 'Changed, refresh and retry',
};

type BulkScheduleType = 'now' | '48h-before' | 'relative';
type BulkUnit = 'hours' | 'days' | 'weeks';

/** Why a session cannot join bulk scheduling; null means selectable. */
function bulkDisabledReason(
  session: EmailSession,
  isHostContext: boolean,
  needsApproval: boolean
): string | null {
  if (session.isCancelled) return 'Cancelled';
  if (session.isPast) return 'Session started';
  if (session.detailEmailSentAt) return 'Already sent';
  if (session.detailSentCount > 0) return 'Already sent';
  if (isProcessing(session.detailEmailScheduledAt)) return 'Sending';
  if (session.hasPendingDetailApproval) return 'Pending review';
  if (
    isHostContext &&
    session.wasReviewedDetail &&
    (session.detailEmailScheduledAt || session.detailEmailSentAt)
  ) {
    return 'Locked by admin review';
  }
  if (isHostContext && needsApproval && session.detailEmailScheduledAt) {
    return 'Scheduled by admin';
  }
  return null;
}

const HOURS_PER_UNIT: Record<BulkUnit, number> = { hours: 1, days: 24, weeks: 24 * 7 };

function sessionDateLabel(session: EmailSession): string {
  return new Date(session.startTime).toLocaleString('en-US', {
    timeZone: DEFAULT_TIMEZONE,
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/**
 * Local preview of the send time; the backend computes the final value
 * from its own clock, so this is an estimate for display only.
 */
function estimateBulkSendTime(
  session: EmailSession,
  type: BulkScheduleType,
  value: number,
  unit: BulkUnit
): Date | null {
  if (type === 'now') return null;
  const hours = type === '48h-before' ? 48 : value * HOURS_PER_UNIT[unit];
  const at = new Date(new Date(session.startTime).getTime() - hours * 60 * 60 * 1000);
  return at.getTime() <= Date.now() ? null : at;
}

function getEmailStatus(
  scheduledAt: string | null,
  sentAt: string | null,
  sentCount: number,
  totalCount: number
): { label: string; color: string } {
  if (isProcessing(scheduledAt)) {
    return { label: 'Sending...', color: 'text-yellow-600' };
  }
  if (sentAt) {
    if (sentCount >= totalCount) {
      return {
        label: `Sent ${new Date(sentAt).toLocaleString('en-US', { timeZone: DEFAULT_TIMEZONE, month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })} (${sentCount}/${totalCount})`,
        color: 'text-green-600',
      };
    }
    return {
      label: `Partial (${sentCount}/${totalCount})`,
      color: 'text-orange-600',
    };
  }
  if (scheduledAt) {
    return {
      label: `Scheduled: ${new Date(scheduledAt).toLocaleString('en-US', { timeZone: DEFAULT_TIMEZONE, month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}`,
      color: 'text-blue-600',
    };
  }
  return { label: 'Not scheduled', color: 'text-gray-400' };
}

export default function AdminEmail() {
  const siteLanguage = useSiteLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const isHostContext = location.pathname.startsWith('/host/');
  const apiPrefix = isHostContext ? 'host' : 'admin';
  const { isAuthenticated, loading: authLoading, getToken } = useAuth();

  const [sessions, setSessions] = useState<EmailSession[]>([]);
  const [needsApproval, setNeedsApproval] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [selectedActivity, setSelectedActivity] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  // Email approvals
  const [pendingApprovals, setPendingApprovals] = useState<EmailApproval[]>([]);
  const [approvalsExpanded, setApprovalsExpanded] = useState(true);
  const [approvalActionLoading, setApprovalActionLoading] = useState<string | null>(null);
  const [approvingApproval, setApprovingApproval] = useState<EmailApproval | null>(null);

  // Detail link editing
  const [editingLinkId, setEditingLinkId] = useState<string | null>(null);
  const [editLinkValue, setEditLinkValue] = useState('');

  // Preview modal
  const [previewSessionId, setPreviewSessionId] = useState<string | null>(null);
  const [previewType, setPreviewType] = useState<'event-detail' | 'reminder'>('event-detail');
  const [previewHtml, setPreviewHtml] = useState<string>('');
  const [previewLoading, setPreviewLoading] = useState(false);

  // Recipients modal
  const [recipientsSessionId, setRecipientsSessionId] = useState<string | null>(null);
  const [recipientsType, setRecipientsType] = useState<'event-detail' | 'reminder'>('event-detail');
  const [recipients, setRecipients] = useState<EmailRecipient[]>([]);
  const [recipientsLoading, setRecipientsLoading] = useState(false);

  // Past sessions toggle
  const [showPast, setShowPast] = useState(false);

  // Bulk scheduling
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkLink, setBulkLink] = useState('');
  const [bulkMessage, setBulkMessage] = useState('');
  const [bulkScheduleType, setBulkScheduleType] = useState<BulkScheduleType>('48h-before');
  const [bulkValue, setBulkValue] = useState('2');
  const [bulkUnit, setBulkUnit] = useState<BulkUnit>('days');
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const [bulkError, setBulkError] = useState<{
    message: string;
    details: BulkErrorDetail[];
  } | null>(null);

  // Schedule modals
  const [showDetailModal, setShowDetailModal] = useState<string | null>(null);
  const [showReminderModal, setShowReminderModal] = useState<string | null>(null);
  const [scheduleType, setScheduleType] = useState<'now' | '48h-before' | 'custom'>('48h-before');
  const [customDateTime, setCustomDateTime] = useState('');

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
    }
  }, [authLoading, isAuthenticated, navigate]);

  const fetchPendingApprovals = async () => {
    if (!isAuthenticated) return;
    try {
      const token = await getToken();
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1';
      const response = await fetch(`${apiUrl}/${apiPrefix}/email-approvals`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to fetch approvals');
      const result = await response.json();
      if (result.success) setPendingApprovals(result.data || []);
    } catch {
      // Silently fail — approvals banner is supplementary
    }
  };

  const fetchSeq = useRef(0);
  const activityRef = useRef(selectedActivity);
  activityRef.current = selectedActivity;

  const fetchSessions = async (showLoading = true) => {
    if (!isAuthenticated) return;
    // A delayed or stale-closure refresh must not overwrite the current
    // list (the bulk UI would offer the wrong sessions): the URL reads the
    // CURRENT filter from a ref, and old responses lose the sequence check
    const seq = ++fetchSeq.current;
    try {
      if (showLoading) setLoading(true);
      const token = await getToken();
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1';
      let url = `${apiUrl}/${apiPrefix}/email/sessions`;
      if (activityRef.current) url += `?activityId=${activityRef.current}`;

      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to fetch sessions');

      const result = await response.json();
      if (seq !== fetchSeq.current) return;
      if (result.success) {
        setSessions(result.data || []);
        if (result.activities) setActivities(result.activities);
        if (result.needsApproval !== undefined) setNeedsApproval(result.needsApproval);
      }
    } catch (err: unknown) {
      if (seq !== fetchSeq.current) return;
      setError(err instanceof Error ? err.message : 'Failed to load');
    } finally {
      if (seq === fetchSeq.current) setLoading(false);
    }
  };

  useEffect(() => {
    fetchSessions(true);
    if (!isHostContext) fetchPendingApprovals();
    // Poll every 30 seconds (without showing loading spinner)
    const interval = setInterval(() => {
      fetchSessions(false);
      if (!isHostContext) fetchPendingApprovals();
    }, 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, getToken, selectedActivity]);

  // Bulk selection lives only inside one activity; switching the filter clears it
  useEffect(() => {
    setSelectedIds(new Set());
    if (!bulkError) setShowBulkModal(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedActivity]);

  // Polling can invalidate selected sessions; prune them. This only cleans
  // unsubmitted selection state and never touches a displayed error panel.
  useEffect(() => {
    setSelectedIds((prev) => {
      if (prev.size === 0) return prev;
      const eligible = new Set(
        sessions.filter((sess) => !cardBulkReason(sess)).map((sess) => sess.id)
      );
      const next = new Set([...prev].filter((id) => eligible.has(id)));
      return next.size === prev.size ? prev : next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessions]);

  useEffect(() => {
    if (showBulkModal && selectedIds.size === 0 && !bulkError && !bulkSubmitting) {
      setShowBulkModal(false);
      setError('Sessions are no longer available for scheduling');
    }
  }, [selectedIds, showBulkModal, bulkError, bulkSubmitting]);

  const cardBulkReason = (sess: EmailSession): string | null => {
    if (sess.activityId !== selectedActivity) return 'Not in this activity';
    return bulkDisabledReason(sess, isHostContext, needsApproval);
  };

  const BULK_MAX = 100;

  const eligibleSessions = sessions.filter((sess) => !cardBulkReason(sess));
  // Select all works on the capped subset, so its checked state (and the
  // clear-on-second-click behavior) must be measured against the same subset
  const cappedEligible = eligibleSessions.slice(0, BULK_MAX);
  const allEligibleSelected =
    cappedEligible.length > 0 && cappedEligible.every((sess) => selectedIds.has(sess.id));

  const toggleSelectAll = () => {
    setSelectedIds(
      allEligibleSelected ? new Set() : new Set(cappedEligible.map((sess) => sess.id))
    );
  };

  const toggleSelect = (sessionId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(sessionId)) next.delete(sessionId);
      else if (next.size < BULK_MAX) next.add(sessionId);
      return next;
    });
  };

  const selectedSessions = sessions
    .filter((sess) => selectedIds.has(sess.id))
    .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());

  const openBulkModal = () => {
    // Prefill with the earliest selected session that already has content
    const seed = selectedSessions.find((sess) => sess.detailLink || sess.detailCustomMessage);
    setBulkLink(seed?.detailLink || '');
    setBulkMessage(seed?.detailCustomMessage || '');
    setBulkScheduleType('48h-before');
    setBulkValue('2');
    setBulkUnit('days');
    setBulkError(null);
    setShowBulkModal(true);
  };

  // Number() accepts browser-valid spellings like "01" or "1.0" that
  // parseInt-plus-string-compare would wrongly reject
  const bulkValueInt = Number(bulkValue);
  const bulkValueValid = Number.isInteger(bulkValueInt) && bulkValueInt >= 1 && bulkValueInt <= 999;
  const bulkLinkTrimmed = bulkLink.trim();
  // Mirrors the backend rules: literal http(s):// prefix, parseable, max 2048
  const bulkLinkValid =
    bulkLinkTrimmed === '' ||
    (bulkLinkTrimmed.length <= 2048 &&
      /^https?:\/\//.test(bulkLinkTrimmed) &&
      (() => {
        try {
          new URL(bulkLinkTrimmed);
          return true;
        } catch {
          return false;
        }
      })());
  const bulkContentValid = bulkLinkTrimmed !== '' || bulkMessage.trim() !== '';
  const bulkConfirmDisabled =
    !bulkContentValid ||
    !bulkLinkValid ||
    selectedIds.size === 0 ||
    (bulkScheduleType === 'relative' && !bulkValueValid);

  const handleBulkSubmit = async () => {
    if (bulkConfirmDisabled || selectedIds.size === 0) return;
    const schedule =
      bulkScheduleType === 'relative' ? { value: bulkValueInt, unit: bulkUnit } : bulkScheduleType;
    try {
      setBulkSubmitting(true);
      setBulkError(null);
      const token = await getToken();
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1';
      // Deliberately not apiCall: that helper throws away the 409 details body
      const response = await fetch(`${apiUrl}/${apiPrefix}/email/sessions/bulk-send-detail`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          sessionIds: [...selectedIds],
          detailLink: bulkLink.trim() || null,
          customMessage: bulkMessage.trim() || null,
          schedule,
        }),
      });
      let result: {
        acceptedCount?: number;
        approvalRequired?: boolean;
        error?: unknown;
        details?: BulkErrorDetail[];
      } | null = null;
      try {
        result = await response.json();
      } catch {
        result = null;
      }
      if (response.ok) {
        setShowBulkModal(false);
        const count = result?.acceptedCount ?? selectedIds.size;
        setSuccessMessage(
          result?.approvalRequired
            ? `Submitted ${count} emails for admin review.`
            : `Scheduled ${count} sessions.`
        );
        setSelectedIds(new Set());
        await fetchSessions(false);
      } else {
        const details = Array.isArray(result?.details) ? result.details : [];
        const rawError = result?.error as unknown;
        const nested = (rawError as { message?: unknown } | null)?.message;
        const message =
          typeof rawError === 'string'
            ? rawError
            : typeof nested === 'string'
              ? nested
              : 'Failed to schedule, please try again';
        setBulkError({ message, details });
        await fetchSessions(false);
      }
    } catch {
      setBulkError({ message: 'Failed to schedule, please try again', details: [] });
    } finally {
      setBulkSubmitting(false);
    }
  };

  const apiCall = async (url: string, method: string, body?: Record<string, unknown>) => {
    const token = await getToken();
    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1';
    const response = await fetch(`${apiUrl}${url}`, {
      method,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: body ? JSON.stringify(body) : undefined,
    });
    const result = await response.json();
    if (!response.ok) {
      const msg =
        typeof result.error === 'string' ? result.error : result.error?.message || 'Request failed';
      throw new Error(msg);
    }
    return result;
  };

  const handleApproveEmail = (approval: EmailApproval) => {
    setApprovingApproval(approval);
    // Pre-fill schedule from host's requested time
    if (approval.scheduledAt) {
      setScheduleType('custom');
      // Format as local datetime for datetime-local input (YYYY-MM-DDTHH:mm)
      const d = new Date(approval.scheduledAt);
      const local = new Date(d.toLocaleString('en-US', { timeZone: DEFAULT_TIMEZONE }));
      const pad = (n: number) => String(n).padStart(2, '0');
      setCustomDateTime(
        `${local.getFullYear()}-${pad(local.getMonth() + 1)}-${pad(local.getDate())}T${pad(local.getHours())}:${pad(local.getMinutes())}`
      );
    } else {
      setScheduleType(approval.emailType === 'detail' ? '48h-before' : 'now');
      setCustomDateTime('');
    }
  };

  const handleConfirmApproval = async () => {
    if (!approvingApproval) return;
    try {
      setApprovalActionLoading(approvingApproval.id);
      let schedule: string;
      if (scheduleType === 'custom') {
        schedule = new Date(customDateTime).toISOString();
      } else {
        schedule = scheduleType === '48h-before' ? '48h-before' : 'now';
      }
      await apiCall(`/${apiPrefix}/email-approvals/${approvingApproval.id}/approve`, 'PUT', {
        schedule,
      });
      setApprovingApproval(null);
      setSuccessMessage('Email approved and scheduled. Host has been notified.');
      await Promise.all([fetchSessions(false), fetchPendingApprovals()]);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to approve');
    } finally {
      setApprovalActionLoading(null);
    }
  };

  const handleDismissApproval = async (approval: EmailApproval) => {
    if (!confirm('Dismiss this email approval? The host will need to resubmit.')) return;
    try {
      setApprovalActionLoading(approval.id);
      await apiCall(`/${apiPrefix}/email-approvals/${approval.id}`, 'DELETE');
      setSuccessMessage('Email approval dismissed.');
      await Promise.all([fetchSessions(false), fetchPendingApprovals()]);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to dismiss');
    } finally {
      setApprovalActionLoading(null);
    }
  };

  const handleSaveDetailLink = async (sessionId: string) => {
    try {
      setActionLoading(sessionId);
      await apiCall(`/${apiPrefix}/email/sessions/${sessionId}/detail-link`, 'PUT', {
        detailLink: editLinkValue,
      });
      setEditingLinkId(null);
      await fetchSessions();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setActionLoading(null);
    }
  };

  const handleSendDetail = async (sessionId: string) => {
    try {
      setActionLoading(sessionId);
      let schedule: string;
      if (scheduleType === 'custom') {
        schedule = new Date(customDateTime).toISOString();
      } else {
        schedule = scheduleType === '48h-before' ? '48h-before' : 'now';
      }
      const result = await apiCall(
        `/${apiPrefix}/email/sessions/${sessionId}/send-detail`,
        'POST',
        { schedule }
      );
      setShowDetailModal(null);

      if (result.approvalRequired) {
        setSuccessMessage(
          'Email content submitted for admin review. You will be notified when approved.'
        );
      }

      await Promise.all([fetchSessions(), ...(!isHostContext ? [fetchPendingApprovals()] : [])]);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to schedule');
    } finally {
      setActionLoading(null);
    }
  };

  const handleSendReminder = async (sessionId: string) => {
    try {
      setActionLoading(sessionId);
      const schedule = scheduleType === 'custom' ? new Date(customDateTime).toISOString() : 'now';
      const result = await apiCall(
        `/${apiPrefix}/email/sessions/${sessionId}/send-reminder`,
        'POST',
        { schedule }
      );
      setShowReminderModal(null);

      if (result.approvalRequired) {
        setSuccessMessage(
          'Email content submitted for admin review. You will be notified when approved.'
        );
      }

      await Promise.all([fetchSessions(), ...(!isHostContext ? [fetchPendingApprovals()] : [])]);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to schedule');
    } finally {
      setActionLoading(null);
    }
  };

  const handleSaveCustomMessage = async (
    sessionId: string,
    emailType: 'detail' | 'reminder',
    message: string | null
  ) => {
    try {
      await apiCall(`/${apiPrefix}/email/sessions/${sessionId}/custom-message`, 'PUT', {
        emailType,
        customMessage: message,
      });
      await fetchSessions(false);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to save message');
    }
  };

  const handlePreview = async (sessionId: string, type: 'event-detail' | 'reminder') => {
    try {
      setPreviewSessionId(sessionId);
      setPreviewType(type);
      setPreviewLoading(true);
      setPreviewHtml('');
      const token = await getToken();
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1';
      const response = await fetch(
        `${apiUrl}/${apiPrefix}/email/sessions/${sessionId}/preview/${type}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (!response.ok) throw new Error('Failed to load preview');
      const html = await response.text();
      setPreviewHtml(html);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load preview');
      setPreviewSessionId(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleViewRecipients = async (sessionId: string, type: 'event-detail' | 'reminder') => {
    try {
      setRecipientsSessionId(sessionId);
      setRecipientsType(type);
      setRecipientsLoading(true);
      setRecipients([]);
      const recipientType = isHostContext && type === 'event-detail' ? 'detail' : type;
      const result = await apiCall(
        `/${apiPrefix}/email/sessions/${sessionId}/recipients/${recipientType}`,
        'GET'
      );
      setRecipients(result.data || []);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load recipients');
      setRecipientsSessionId(null);
    } finally {
      setRecipientsLoading(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <AdminLayout title="Email Management" navItems={isHostContext ? hostNavItems : undefined}>
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
          {error}
          <button onClick={() => setError(null)} className="ml-2 underline">
            Dismiss
          </button>
        </div>
      )}

      {successMessage && (
        <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4 text-green-800">
          {successMessage}
          <button onClick={() => setSuccessMessage(null)} className="ml-2 underline">
            Dismiss
          </button>
        </div>
      )}

      {/* Pending Email Approvals (admin only) */}
      {!isHostContext && pendingApprovals.length > 0 && (
        <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-lg">
          <button
            onClick={() => setApprovalsExpanded(!approvalsExpanded)}
            className="w-full flex items-center justify-between p-4"
          >
            <div className="flex items-center gap-2">
              <span className="text-yellow-600 font-medium">
                {pendingApprovals.length} email(s) pending approval
              </span>
            </div>
            <svg
              className={`w-5 h-5 text-yellow-600 transition-transform ${approvalsExpanded ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>
          {approvalsExpanded && (
            <div className="px-4 pb-4 space-y-3">
              {pendingApprovals.map((approval) => (
                <div key={approval.id} className="bg-white rounded-lg border border-yellow-100 p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-medium text-gray-900">
                          {approval.hostName}
                        </span>
                        <span className="text-xs px-2 py-0.5 bg-yellow-100 text-yellow-800 rounded-full">
                          {approval.emailType === 'detail' ? 'Event Detail' : 'Reminder'}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600">
                        {approval.activityTitle}
                        {approval.activityTitleEN ? ` (${approval.activityTitleEN})` : ''}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        Session:{' '}
                        {new Date(approval.sessionStartTime).toLocaleString('en-US', {
                          timeZone: DEFAULT_TIMEZONE,
                          weekday: 'short',
                          month: 'short',
                          day: 'numeric',
                          hour: 'numeric',
                          minute: '2-digit',
                        })}
                      </p>
                      <p className="text-xs text-gray-400 mt-1">
                        Submitted{' '}
                        {new Date(approval.createdAt).toLocaleString('en-US', {
                          timeZone: DEFAULT_TIMEZONE,
                          month: 'short',
                          day: 'numeric',
                          hour: 'numeric',
                          minute: '2-digit',
                        })}{' '}
                        · Edit content in session card below before approving
                      </p>
                    </div>
                    <div className="ml-4 flex flex-col gap-2">
                      <button
                        onClick={() => handleApproveEmail(approval)}
                        disabled={approvalActionLoading === approval.id}
                        className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50 whitespace-nowrap"
                      >
                        {approvalActionLoading === approval.id ? 'Approving...' : 'Approve'}
                      </button>
                      <button
                        onClick={() => handleDismissApproval(approval)}
                        disabled={approvalActionLoading === approval.id}
                        className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50 whitespace-nowrap"
                      >
                        Dismiss
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Activity Filter */}
      <div className="bg-white rounded-lg shadow-sm mb-6 p-4" {...tourAttr(TOUR.emailFilter)}>
        <label className="block text-sm font-medium text-gray-700 mb-2">Filter by Activity</label>
        <select
          value={selectedActivity}
          onChange={(e) => setSelectedActivity(e.target.value)}
          className="w-full max-w-md border border-gray-300 rounded-lg px-3 py-2 text-sm"
        >
          <option value="">All Activities</option>
          {activities.map((a) => (
            <option key={a.id} value={a.id}>
              {localize(a.titleCN, a.titleEN, siteLanguage)}
            </option>
          ))}
        </select>
      </div>

      {/* Bulk selection toolbar: only within a single selected activity */}
      {selectedActivity && sessions.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm mb-4 p-4" {...tourAttr(TOUR.emailBulk)}>
          <div className="flex items-start justify-between gap-4">
            <h3 className="text-sm font-semibold text-gray-900">Bulk schedule detail email</h3>
            <button
              onClick={openBulkModal}
              disabled={selectedIds.size === 0}
              className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary/90 disabled:opacity-50 whitespace-nowrap"
            >
              Schedule detail email{selectedIds.size > 0 ? ` (${selectedIds.size})` : ''}
            </button>
          </div>
          <label className="mt-3 flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={allEligibleSelected}
              disabled={eligibleSessions.length === 0}
              onChange={toggleSelectAll}
            />
            Select all
            <span className="text-gray-500">
              ({selectedIds.size} selected{selectedIds.size >= 100 ? ', max 100 per batch' : ''})
            </span>
          </label>
        </div>
      )}

      {/* Session Cards */}
      {sessions.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm p-12 text-center text-gray-500">
          <p>No sessions found</p>
        </div>
      ) : (
        <>
          {/* Upcoming Sessions */}
          {sessions.filter((s) => !s.isPast).length > 0 && (
            <div className="space-y-4">
              {sessions
                .filter((s) => !s.isPast)
                .map((session) => (
                  <SessionCard
                    key={session.id}
                    session={session}
                    isHostContext={isHostContext}
                    needsApproval={needsApproval}
                    bulkMode={!!selectedActivity}
                    bulkChecked={selectedIds.has(session.id)}
                    bulkDisabledReason={cardBulkReason(session)}
                    onToggleSelect={() => toggleSelect(session.id)}
                    editingLinkId={editingLinkId}
                    editLinkValue={editLinkValue}
                    actionLoading={actionLoading}
                    onEditLink={(id, link) => {
                      setEditingLinkId(id);
                      setEditLinkValue(link);
                    }}
                    onCancelEditLink={() => setEditingLinkId(null)}
                    onSaveDetailLink={handleSaveDetailLink}
                    onEditLinkValueChange={setEditLinkValue}
                    onShowDetailModal={(id) => {
                      setShowDetailModal(id);
                      setScheduleType('48h-before');
                      setCustomDateTime('');
                    }}
                    onShowReminderModal={(id) => {
                      setShowReminderModal(id);
                      setScheduleType('custom');
                      setCustomDateTime('');
                    }}
                    onSaveCustomMessage={handleSaveCustomMessage}
                    onPreview={handlePreview}
                    onViewRecipients={handleViewRecipients}
                  />
                ))}
            </div>
          )}

          {/* Past Sessions */}
          {sessions.filter((s) => s.isPast).length > 0 && (
            <div className="mt-8">
              <button
                onClick={() => setShowPast(!showPast)}
                className="flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-gray-700 mb-4"
              >
                <svg
                  className={`w-4 h-4 transition-transform ${showPast ? 'rotate-90' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
                Past Sessions ({sessions.filter((s) => s.isPast).length})
              </button>
              {showPast && (
                <div className="space-y-4">
                  {sessions
                    .filter((s) => s.isPast)
                    .map((session) => (
                      <SessionCard
                        key={session.id}
                        session={session}
                        isHostContext={isHostContext}
                        needsApproval={needsApproval}
                        bulkMode={!!selectedActivity}
                        bulkChecked={selectedIds.has(session.id)}
                        bulkDisabledReason={cardBulkReason(session)}
                        onToggleSelect={() => toggleSelect(session.id)}
                        editingLinkId={editingLinkId}
                        editLinkValue={editLinkValue}
                        actionLoading={actionLoading}
                        onEditLink={(id, link) => {
                          setEditingLinkId(id);
                          setEditLinkValue(link);
                        }}
                        onCancelEditLink={() => setEditingLinkId(null)}
                        onSaveDetailLink={handleSaveDetailLink}
                        onEditLinkValueChange={setEditLinkValue}
                        onShowDetailModal={(id) => {
                          setShowDetailModal(id);
                          setScheduleType('48h-before');
                          setCustomDateTime('');
                        }}
                        onShowReminderModal={(id) => {
                          setShowReminderModal(id);
                          setScheduleType('custom');
                          setCustomDateTime('');
                        }}
                        onSaveCustomMessage={handleSaveCustomMessage}
                        onPreview={handlePreview}
                        onViewRecipients={handleViewRecipients}
                      />
                    ))}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Bulk Schedule Modal */}
      {showBulkModal && (
        <Modal
          title={`Schedule Event Detail Email (${selectedSessions.length} sessions)`}
          wide
          onClose={() => {
            if (bulkSubmitting) return;
            setShowBulkModal(false);
            setBulkError(null);
          }}
          onConfirm={handleBulkSubmit}
          loading={bulkSubmitting}
          confirmDisabled={bulkConfirmDisabled}
          confirmText={`Schedule for ${selectedSessions.length} sessions`}
        >
          <div className="space-y-4">
            {bulkError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <div className="flex items-start justify-between gap-2">
                  <p className="text-sm font-medium text-red-800">{bulkError.message}</p>
                  <button
                    onClick={() => setBulkError(null)}
                    className="text-xs text-red-700 underline whitespace-nowrap"
                  >
                    Dismiss
                  </button>
                </div>
                {bulkError.details.length > 0 && (
                  <ul className="mt-2 space-y-1">
                    {bulkError.details.map((d) => (
                      <li key={d.sessionId} className="text-xs text-red-700">
                        {new Date(d.startTime).toLocaleString('en-US', {
                          timeZone: DEFAULT_TIMEZONE,
                          month: 'short',
                          day: 'numeric',
                          hour: 'numeric',
                          minute: '2-digit',
                        })}
                        {': '}
                        {BULK_REASON_LABELS[d.reason] || d.reason}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
            {isHostContext && needsApproval && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-sm text-amber-800">
                  Your emails will be reviewed by an admin before they are sent. Once submitted, the
                  detail link and message can no longer be edited.
                </p>
              </div>
            )}
            <div>
              <label
                htmlFor="bulk-detail-link"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Detail Link
              </label>
              <input
                id="bulk-detail-link"
                type="url"
                value={bulkLink}
                onChange={(e) => setBulkLink(e.target.value)}
                placeholder="https://..."
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              />
              {!bulkLinkValid && (
                <p className="text-xs text-red-600 mt-1">
                  Link must start with http:// or https:// and be at most 2048 characters
                </p>
              )}
            </div>
            <div>
              <label
                htmlFor="bulk-detail-message"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Message
              </label>
              <textarea
                id="bulk-detail-message"
                value={bulkMessage}
                onChange={(e) => setBulkMessage(e.target.value)}
                rows={3}
                maxLength={3000}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              />
              <p className="text-xs text-gray-500 mt-1">
                This will replace the link and message on all selected sessions.
              </p>
            </div>
            <div className="space-y-2">
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  checked={bulkScheduleType === '48h-before'}
                  onChange={() => setBulkScheduleType('48h-before')}
                />
                <span className="text-sm">48 hours before session</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  checked={bulkScheduleType === 'relative'}
                  onChange={() => setBulkScheduleType('relative')}
                />
                <span className="text-sm flex items-center gap-2">
                  <input
                    type="number"
                    min={1}
                    max={999}
                    value={bulkValue}
                    onFocus={() => setBulkScheduleType('relative')}
                    onChange={(e) => setBulkValue(e.target.value)}
                    className="w-20 border border-gray-300 rounded-lg px-2 py-1 text-sm"
                  />
                  <select
                    value={bulkUnit}
                    onFocus={() => setBulkScheduleType('relative')}
                    onChange={(e) => {
                      setBulkScheduleType('relative');
                      setBulkUnit(e.target.value as BulkUnit);
                    }}
                    className="border border-gray-300 rounded-lg px-2 py-1 text-sm"
                  >
                    <option value="hours">hours</option>
                    <option value="days">days</option>
                    <option value="weeks">weeks</option>
                  </select>
                  before session start
                </span>
              </label>
              {bulkScheduleType === 'relative' && !bulkValueValid && (
                <p className="text-xs text-red-600 ml-6">Enter a whole number from 1 to 999</p>
              )}
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  checked={bulkScheduleType === 'now'}
                  onChange={() => setBulkScheduleType('now')}
                />
                <span className="text-sm">Send Now</span>
              </label>
            </div>
            <div className="border rounded-lg divide-y max-h-48 overflow-y-auto">
              {selectedSessions.map((sess) => {
                const estimate = estimateBulkSendTime(
                  sess,
                  bulkScheduleType,
                  bulkValueValid ? bulkValueInt : 1,
                  bulkUnit
                );
                return (
                  <div
                    key={sess.id}
                    className="flex items-center justify-between px-3 py-2 text-sm"
                  >
                    <span className="text-gray-700">
                      {new Date(sess.startTime).toLocaleString('en-US', {
                        timeZone: DEFAULT_TIMEZONE,
                        weekday: 'short',
                        month: 'short',
                        day: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit',
                      })}
                    </span>
                    {estimate ? (
                      <span className="text-gray-500">
                        {estimate.toLocaleString('en-US', {
                          timeZone: DEFAULT_TIMEZONE,
                          month: 'short',
                          day: 'numeric',
                          hour: 'numeric',
                          minute: '2-digit',
                        })}
                      </span>
                    ) : (
                      <span className="text-orange-600 font-medium">
                        Will send in the next batch
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </Modal>
      )}

      {/* Schedule Detail Modal */}
      {showDetailModal && (
        <Modal
          title="Schedule Event Detail Email"
          onClose={() => {
            setShowDetailModal(null);
          }}
          onConfirm={() => handleSendDetail(showDetailModal)}
          loading={actionLoading === showDetailModal}
        >
          <div className="space-y-3">
            {isHostContext && needsApproval && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-sm text-amber-800">
                  Once scheduled, the detail link and message can no longer be edited. Please use
                  the Preview button to review the email before scheduling. Your email will be
                  reviewed by an admin before it is sent.
                </p>
              </div>
            )}
            <SchedulePicker
              scheduleType={scheduleType}
              customDateTime={customDateTime}
              onScheduleTypeChange={setScheduleType}
              onCustomDateTimeChange={setCustomDateTime}
            />
          </div>
        </Modal>
      )}

      {/* Preview Modal */}
      {previewSessionId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-2xl h-[80vh] flex flex-col">
            <div className="flex items-center justify-between p-4">
              <h2 className="text-lg font-semibold text-gray-900">
                {previewType === 'event-detail' ? 'Event Detail' : 'Reminder'} Email Preview
              </h2>
              <button
                onClick={() => setPreviewSessionId(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>
            <div className="flex-1 overflow-hidden">
              {previewLoading ? (
                <div className="flex items-center justify-center h-full">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              ) : (
                <iframe
                  srcDoc={previewHtml}
                  title="Email Preview"
                  className="w-full h-full border-0"
                  sandbox=""
                />
              )}
            </div>
          </div>
        </div>
      )}

      {/* Recipients Modal */}
      {recipientsSessionId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-lg max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between p-4">
              <h2 className="text-lg font-semibold text-gray-900">
                {recipientsType === 'event-detail' ? 'Event Detail' : 'Reminder'} Recipients
              </h2>
              <button
                onClick={() => setRecipientsSessionId(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>
            <div className="flex-1 overflow-auto p-4">
              {recipientsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              ) : recipients.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No recipients found</p>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left">
                      <th className="pb-2 font-medium text-gray-500">Name</th>
                      {!isHostContext && <th className="pb-2 font-medium text-gray-500">Email</th>}
                      <th className="pb-2 font-medium text-gray-500">Sent At</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recipients.map((r) => (
                      <tr key={r.bookingId} className="border-b last:border-0">
                        <td className="py-2 pr-3">{r.name}</td>
                        {!isHostContext && <td className="py-2 pr-3 text-gray-600">{r.email}</td>}
                        <td className="py-2 text-gray-500 whitespace-nowrap">
                          {new Date(r.sentAt).toLocaleString('en-US', {
                            timeZone: DEFAULT_TIMEZONE,
                            month: 'short',
                            day: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit',
                          })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Send Reminder Modal */}
      {showReminderModal && (
        <Modal
          title="Send Reminder Email"
          onClose={() => {
            setShowReminderModal(null);
          }}
          onConfirm={() => handleSendReminder(showReminderModal)}
          loading={actionLoading === showReminderModal}
        >
          <div className="space-y-3">
            {isHostContext && needsApproval && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-sm text-amber-800">
                  Once scheduled, the message can no longer be edited. Please use the Preview button
                  to review the email before scheduling. Your email will be reviewed by an admin
                  before it is sent.
                </p>
              </div>
            )}
            <SchedulePicker
              scheduleType={scheduleType}
              customDateTime={customDateTime}
              onScheduleTypeChange={setScheduleType}
              onCustomDateTimeChange={setCustomDateTime}
              show48hBefore={false}
            />
          </div>
        </Modal>
      )}
      {/* Approve Schedule Modal */}
      {approvingApproval && (
        <Modal
          title={`Approve & Schedule ${approvingApproval.emailType === 'detail' ? 'Event Detail' : 'Reminder'} Email`}
          onClose={() => setApprovingApproval(null)}
          onConfirm={handleConfirmApproval}
          loading={approvalActionLoading === approvingApproval.id}
          confirmText="Approve & Schedule"
        >
          <div className="space-y-3">
            <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-600">
              <p>
                <strong>Host:</strong> {approvingApproval.hostName}
              </p>
              <p>
                <strong>Activity:</strong> {approvingApproval.activityTitle}
              </p>
            </div>
            <SchedulePicker
              scheduleType={scheduleType}
              customDateTime={customDateTime}
              onScheduleTypeChange={setScheduleType}
              onCustomDateTimeChange={setCustomDateTime}
            />
          </div>
        </Modal>
      )}
    </AdminLayout>
  );
}

function SchedulePicker({
  scheduleType,
  customDateTime,
  onScheduleTypeChange,
  onCustomDateTimeChange,
  show48hBefore = true,
}: {
  scheduleType: 'now' | '48h-before' | 'custom';
  customDateTime: string;
  onScheduleTypeChange: (type: 'now' | '48h-before' | 'custom') => void;
  onCustomDateTimeChange: (value: string) => void;
  show48hBefore?: boolean;
}) {
  return (
    <>
      {show48hBefore && (
        <label className="flex items-center gap-2">
          <input
            type="radio"
            checked={scheduleType === '48h-before'}
            onChange={() => onScheduleTypeChange('48h-before')}
          />
          <span className="text-sm">48 hours before session</span>
        </label>
      )}
      <label className="flex items-center gap-2">
        <input
          type="radio"
          checked={scheduleType === 'custom'}
          onChange={() => onScheduleTypeChange('custom')}
        />
        <span className="text-sm">Pick a time</span>
      </label>
      {scheduleType === 'custom' && (
        <input
          type="datetime-local"
          value={customDateTime}
          onChange={(e) => onCustomDateTimeChange(e.target.value)}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
        />
      )}
      <label className="flex items-center gap-2">
        <input
          type="radio"
          checked={scheduleType === 'now'}
          onChange={() => onScheduleTypeChange('now')}
        />
        <span className="text-sm">Send Now</span>
      </label>
    </>
  );
}

function SessionCard({
  session,
  isHostContext,
  needsApproval,
  bulkMode,
  bulkChecked,
  bulkDisabledReason: bulkReason,
  onToggleSelect,
  editingLinkId,
  editLinkValue,
  actionLoading,
  onEditLink,
  onCancelEditLink,
  onSaveDetailLink,
  onEditLinkValueChange,
  onShowDetailModal,
  onShowReminderModal,
  onSaveCustomMessage,
  onPreview,
  onViewRecipients,
}: {
  session: EmailSession;
  isHostContext: boolean;
  needsApproval: boolean;
  bulkMode: boolean;
  bulkChecked: boolean;
  bulkDisabledReason: string | null;
  onToggleSelect: () => void;
  editingLinkId: string | null;
  editLinkValue: string;
  actionLoading: string | null;
  onEditLink: (id: string, link: string) => void;
  onCancelEditLink: () => void;
  onSaveDetailLink: (id: string) => void;
  onEditLinkValueChange: (value: string) => void;
  onShowDetailModal: (id: string) => void;
  onShowReminderModal: (id: string) => void;
  onSaveCustomMessage: (
    id: string,
    emailType: 'detail' | 'reminder',
    message: string | null
  ) => Promise<void>;
  onPreview: (id: string, type: 'event-detail' | 'reminder') => void;
  onViewRecipients: (id: string, type: 'event-detail' | 'reminder') => void;
}) {
  const siteLanguage = useSiteLanguage();
  const [editingDetailMsg, setEditingDetailMsg] = useState(false);
  const [editingReminderMsg, setEditingReminderMsg] = useState(false);
  const [detailMsgDraft, setDetailMsgDraft] = useState('');
  const [reminderMsgDraft, setReminderMsgDraft] = useState('');
  const [savingMessage, setSavingMessage] = useState<string | null>(null);

  const detailStatus = getEmailStatus(
    session.detailEmailScheduledAt,
    session.detailEmailSentAt,
    session.detailSentCount,
    session.confirmedCount
  );
  const reminderStatus = getEmailStatus(
    session.reminderScheduledAt,
    session.reminderSentAt,
    session.reminderSentCount,
    session.confirmedCount
  );

  const isDetailProcessing = isProcessing(session.detailEmailScheduledAt);
  const isReminderProcessing = isProcessing(session.reminderScheduledAt);

  const detailFullySent =
    session.detailEmailSentAt && session.detailSentCount >= session.confirmedCount;
  const reminderFullySent =
    session.reminderSentAt && session.reminderSentCount >= session.confirmedCount;

  const hasPartialDetail =
    session.detailEmailSentAt && session.detailSentCount < session.confirmedCount;
  const hasPartialReminder =
    session.reminderSentAt && session.reminderSentCount < session.confirmedCount;

  const isDisabled = session.isCancelled || session.isPast;

  // Host editing locks:
  // - Pending approval always locks
  // - Emails that went through review (wasReviewed) stay locked even after threshold is reached
  const hostDetailLocked =
    isHostContext &&
    !!(
      session.hasPendingDetailApproval ||
      (session.wasReviewedDetail &&
        (session.detailEmailScheduledAt || session.detailEmailSentAt)) ||
      // Below-threshold hosts cannot touch an actively scheduled email;
      // the backend enforces the same rule with a 409
      (needsApproval && session.detailEmailScheduledAt)
    );
  const hostReminderLocked =
    isHostContext &&
    !!(
      session.hasPendingReminderApproval ||
      (session.wasReviewedReminder && (session.reminderScheduledAt || session.reminderSentAt))
    );

  const handleSaveMessage = async (emailType: 'detail' | 'reminder') => {
    const draft = emailType === 'detail' ? detailMsgDraft : reminderMsgDraft;
    setSavingMessage(emailType);
    try {
      await onSaveCustomMessage(session.id, emailType, draft || null);
      if (emailType === 'detail') setEditingDetailMsg(false);
      else setEditingReminderMsg(false);
    } finally {
      setSavingMessage(null);
    }
  };

  return (
    <div
      className={`bg-white rounded-lg shadow-sm p-6 ${isDisabled ? 'opacity-60' : ''}`}
      {...tourAttr(TOUR.emailSessionCard)}
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-start gap-3">
          {bulkMode && (
            <span title={bulkReason ?? undefined} className="mt-1.5 inline-flex">
              <input
                type="checkbox"
                checked={bulkChecked}
                disabled={!!bulkReason}
                onChange={onToggleSelect}
                aria-label={
                  bulkReason
                    ? `Cannot select session ${sessionDateLabel(session)}: ${bulkReason}`
                    : `Select session ${sessionDateLabel(session)}`
                }
              />
            </span>
          )}
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              {bilingualLines(session.activityTitleCN, session.activityTitleEN, siteLanguage)[0]}
            </h3>
            {bilingualLines(session.activityTitleCN, session.activityTitleEN, siteLanguage)[1] && (
              <p className="text-sm text-gray-500">
                {bilingualLines(session.activityTitleCN, session.activityTitleEN, siteLanguage)[1]}
              </p>
            )}
            <p className="text-sm text-gray-600 mt-1">
              {new Date(session.startTime).toLocaleDateString('en-US', {
                timeZone: DEFAULT_TIMEZONE,
                weekday: 'short',
                month: 'short',
                day: 'numeric',
                year: 'numeric',
              })}{' '}
              {new Date(session.startTime).toLocaleTimeString('en-US', {
                timeZone: DEFAULT_TIMEZONE,
                hour: 'numeric',
                minute: '2-digit',
              })}{' '}
              -{' '}
              {new Date(session.endTime).toLocaleTimeString('en-US', {
                timeZone: DEFAULT_TIMEZONE,
                hour: 'numeric',
                minute: '2-digit',
              })}
            </p>
          </div>
        </div>
        <div className="text-right flex items-center gap-2">
          <span className="text-sm font-medium text-gray-700">
            {session.confirmedCount} confirmed
          </span>
          {session.isCancelled && (
            <span className="text-xs px-2 py-1 bg-red-100 text-red-800 rounded-full">
              Cancelled
            </span>
          )}
          {session.isPast && !session.isCancelled && (
            <span className="text-xs px-2 py-1 bg-gray-100 text-gray-500 rounded-full">Past</span>
          )}
        </div>
      </div>

      {/* Detail Link */}
      <div className="mb-4 pb-4" {...tourAttr(TOUR.emailDetailLink)}>
        <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">
          Detail Link
        </label>
        {editingLinkId === session.id ? (
          <div className="flex items-center gap-2 mt-1">
            <input
              type="url"
              value={editLinkValue}
              onChange={(e) => onEditLinkValueChange(e.target.value)}
              placeholder="https://..."
              className="flex-1 border border-gray-300 rounded-lg px-3 py-1.5 text-sm"
            />
            <button
              onClick={() => onSaveDetailLink(session.id)}
              disabled={actionLoading === session.id}
              className="px-3 py-1.5 bg-primary text-white text-sm rounded-lg hover:bg-primary/90 disabled:opacity-50"
            >
              Save
            </button>
            <button onClick={onCancelEditLink} className="px-3 py-1.5 text-gray-600 text-sm">
              Cancel
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-2 mt-1">
            {session.detailLink ? (
              <>
                <a
                  href={session.detailLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-primary hover:underline truncate max-w-md"
                >
                  {session.detailLink}
                </a>
                {!isDisabled && !hostDetailLocked && (
                  <button
                    onClick={() => onEditLink(session.id, session.detailLink || '')}
                    className="text-xs text-primary hover:text-primary/80"
                  >
                    Edit
                  </button>
                )}
              </>
            ) : !isDisabled && !hostDetailLocked ? (
              <button
                onClick={() => onEditLink(session.id, '')}
                className="text-xs text-primary hover:text-primary/80"
              >
                + Add Link
              </button>
            ) : (
              <span className="text-sm text-gray-400">Not set</span>
            )}
          </div>
        )}
      </div>

      {/* Email Status */}
      <div className="grid grid-cols-2 gap-4">
        {/* Event Detail */}
        <div {...tourAttr(TOUR.emailDetailCol)}>
          <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">
            Event Detail
          </label>
          {session.hasPendingDetailApproval ? (
            <p className="text-sm font-medium mt-1 text-amber-600">
              {isHostContext ? 'Pending admin approval' : 'Pending approval'}
            </p>
          ) : (
            <p className={`text-sm font-medium mt-1 ${detailStatus.color}`}>{detailStatus.label}</p>
          )}

          {/* Custom Message (inline) */}
          {editingDetailMsg ? (
            <div className="mt-2">
              <textarea
                value={detailMsgDraft}
                onChange={(e) => {
                  if (e.target.value.length <= 3000) setDetailMsgDraft(e.target.value);
                }}
                placeholder="Add activity details for participants..."
                rows={4}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              />
              <div className="flex items-center justify-between mt-1">
                <p
                  className={`text-xs ${detailMsgDraft.length > 2700 ? 'text-orange-500' : 'text-gray-400'}`}
                >
                  {detailMsgDraft.length}/3000
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setEditingDetailMsg(false)}
                    className="text-xs px-3 py-1 text-gray-600"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleSaveMessage('detail')}
                    disabled={savingMessage === 'detail'}
                    className="text-xs px-3 py-1 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50"
                  >
                    {savingMessage === 'detail' ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </div>
            </div>
          ) : session.detailCustomMessage ? (
            <div className="mt-2 bg-gray-50 rounded-lg p-2">
              <p className="text-xs text-gray-600 line-clamp-3">{session.detailCustomMessage}</p>
              {!isDisabled && !hostDetailLocked && (
                <div className="flex gap-2 mt-1">
                  <button
                    onClick={() => {
                      setDetailMsgDraft(session.detailCustomMessage || '');
                      setEditingDetailMsg(true);
                    }}
                    className="text-xs text-gray-500 hover:text-gray-700"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => onSaveCustomMessage(session.id, 'detail', null)}
                    className="text-xs text-red-500 hover:text-red-700"
                  >
                    Remove
                  </button>
                </div>
              )}
            </div>
          ) : !isDisabled && !hostDetailLocked ? (
            <button
              onClick={() => {
                setDetailMsgDraft('');
                setEditingDetailMsg(true);
              }}
              className="text-xs text-primary hover:text-primary/80 mt-2"
            >
              + Add Message
            </button>
          ) : null}

          <div className="flex flex-wrap gap-2 mt-2" {...tourAttr(TOUR.emailDetailActions)}>
            {!session.isPast && (
              <button
                onClick={() => onShowDetailModal(session.id)}
                disabled={
                  isDetailProcessing ||
                  !!detailFullySent ||
                  session.isCancelled ||
                  (hostDetailLocked && !hasPartialDetail)
                }
                className="text-xs px-3 py-1.5 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {hasPartialDetail
                  ? `Retry (${session.confirmedCount - session.detailSentCount} failed)`
                  : 'Schedule'}
              </button>
            )}
            <button
              onClick={() => onPreview(session.id, 'event-detail')}
              className="text-xs px-3 py-1.5 border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50"
            >
              Preview
            </button>
            {session.detailSentCount > 0 && (
              <button
                onClick={() => onViewRecipients(session.id, 'event-detail')}
                className="text-xs px-3 py-1.5 border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50"
              >
                Recipients ({session.detailSentCount})
              </button>
            )}
          </div>
        </div>

        {/* Reminder */}
        <div {...tourAttr(TOUR.emailReminderCol)}>
          <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">
            Reminder
          </label>
          {session.hasPendingReminderApproval ? (
            <p className="text-sm font-medium mt-1 text-amber-600">
              {isHostContext ? 'Pending admin approval' : 'Pending approval'}
            </p>
          ) : (
            <p className={`text-sm font-medium mt-1 ${reminderStatus.color}`}>
              {reminderStatus.label}
            </p>
          )}

          {/* Custom Message (inline) */}
          {editingReminderMsg ? (
            <div className="mt-2">
              <textarea
                value={reminderMsgDraft}
                onChange={(e) => {
                  if (e.target.value.length <= 500) setReminderMsgDraft(e.target.value);
                }}
                placeholder="Add a custom message for participants..."
                rows={3}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              />
              <div className="flex items-center justify-between mt-1">
                <p
                  className={`text-xs ${reminderMsgDraft.length > 450 ? 'text-orange-500' : 'text-gray-400'}`}
                >
                  {reminderMsgDraft.length}/500
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setEditingReminderMsg(false)}
                    className="text-xs px-3 py-1 text-gray-600"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleSaveMessage('reminder')}
                    disabled={savingMessage === 'reminder'}
                    className="text-xs px-3 py-1 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    {savingMessage === 'reminder' ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </div>
            </div>
          ) : session.reminderCustomMessage ? (
            <div className="mt-2 bg-gray-50 rounded-lg p-2">
              <p className="text-xs text-gray-600 line-clamp-3">{session.reminderCustomMessage}</p>
              {!isDisabled && !hostReminderLocked && (
                <div className="flex gap-2 mt-1">
                  <button
                    onClick={() => {
                      setReminderMsgDraft(session.reminderCustomMessage || '');
                      setEditingReminderMsg(true);
                    }}
                    className="text-xs text-gray-500 hover:text-gray-700"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => onSaveCustomMessage(session.id, 'reminder', null)}
                    className="text-xs text-red-500 hover:text-red-700"
                  >
                    Remove
                  </button>
                </div>
              )}
            </div>
          ) : !isDisabled && !hostReminderLocked ? (
            <button
              onClick={() => {
                setReminderMsgDraft('');
                setEditingReminderMsg(true);
              }}
              className="text-xs text-blue-600 hover:text-blue-500 mt-2"
            >
              + Add Message
            </button>
          ) : null}

          <div className="flex flex-wrap gap-2 mt-2">
            {!session.isPast && (
              <button
                onClick={() => onShowReminderModal(session.id)}
                disabled={
                  isReminderProcessing ||
                  !!reminderFullySent ||
                  session.isCancelled ||
                  (hostReminderLocked && !hasPartialReminder)
                }
                className="text-xs px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {hasPartialReminder
                  ? `Retry (${session.confirmedCount - session.reminderSentCount} failed)`
                  : 'Schedule'}
              </button>
            )}
            <button
              onClick={() => onPreview(session.id, 'reminder')}
              className="text-xs px-3 py-1.5 border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50"
            >
              Preview
            </button>
            {session.reminderSentCount > 0 && (
              <button
                onClick={() => onViewRecipients(session.id, 'reminder')}
                className="text-xs px-3 py-1.5 border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50"
              >
                Recipients ({session.reminderSentCount})
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Modal({
  title,
  children,
  onClose,
  onConfirm,
  loading,
  confirmText,
  confirmDisabled = false,
  wide = false,
}: {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
  onConfirm: () => void;
  loading: boolean;
  confirmText?: string;
  confirmDisabled?: boolean;
  wide?: boolean;
}) {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div
        className={`bg-white rounded-lg ${wide ? 'max-w-2xl max-h-[90vh] overflow-y-auto' : 'max-w-md'} w-full`}
      >
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              ✕
            </button>
          </div>
          {children}
          <div className="flex justify-end gap-3 mt-6">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              disabled={loading || confirmDisabled}
              className="px-4 py-2 text-sm text-white bg-primary rounded-lg hover:bg-primary/90 disabled:opacity-50"
            >
              {loading ? 'Processing...' : confirmText || 'Confirm'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
